import { fastqSampleName } from 'src/utils/fastqNaming'
import {
  GROUP_ANALYSES,
  WORKFLOW_DEFAULTS,
  aiAvailable,
  isTumourRna,
  normalLaneIdsOf,
  requestedLongRead,
  sampleRunsAI,
  validateSample,
} from 'src/services/analysisStart'

// ----------------------------------------------------------------------
// Samples in the New analysis panel. A sample is built from one or more rows of
// the /file/list response (FASTQ lanes of one sample are grouped). Everything
// here is pure so the panel and the sheet import share one model.
// ----------------------------------------------------------------------

export const FILE_TYPE_LABEL = {
  fastq: 'FASTQ',
  vcf: 'VCF',
  ont_bam: 'ONT BAM',
  pacbio_bam: 'PacBio BAM',
}

// The workflow input each file type feeds.
export const INPUT_OF_FILE_TYPE = { fastq: 'fastq', vcf: 'vcf', ont_bam: 'ont', pacbio_bam: 'pacbio' }

/** The input a sample feeds ('fastq' | 'ont' | 'pacbio' | 'vcf'), or null. */
export const inputOfSample = (sample) =>
  INPUT_OF_FILE_TYPE[
    String(sample?.fileType || '')
      .toLowerCase()
      .replace(/^ont$/, 'ont_bam')
      .replace(/^pacbio$/, 'pacbio_bam')
  ] || null

// Karyotypes the patient details endpoint accepts (SampleDetailsPanel).
export const SEX_OPTIONS = [
  { value: '', label: 'Unknown' },
  { value: 'XX', label: 'Female (XX)' },
  { value: 'XY', label: 'Male (XY)' },
  { value: 'XXX', label: 'XXX' },
  { value: 'XYY', label: 'XYY' },
  { value: 'XXY', label: 'XXY' },
  { value: 'XXXX', label: 'XXXX' },
  { value: 'XXXY', label: 'XXXY' },
  { value: 'XXYY', label: 'XXYY' },
  { value: 'XXXXX', label: 'XXXXX' },
  { value: 'XXXXY', label: 'XXXXY' },
]
// Patient details are saved through /file/details, which writes FASTQ pairs and
// VCFs only (savePatientFields in src/services/analysisStart.js). Long-read files
// show their saved details read-only.
export const canSavePatient = (sample) => sample?.fileType === 'fastq' || sample?.fileType === 'vcf'

export const SEX_SHORT = { '': 'Unknown', XX: 'Female', XY: 'Male' }
export const sexShortLabel = (value) => SEX_SHORT[value || ''] || value

// Row id of a /file/list row. The row type decides the id: an ONT or PacBio row may
// also carry the vcf_id of its generated VCF.
export const fileIdOf = (row) => {
  if (!row) return null
  switch (row.file_type) {
    case 'fastq':
      return row.fastq_pair_id || row.fastq_file_id || null
    case 'vcf':
      return row.vcf_id || null
    case 'ont_bam':
      return row.ont_bam_id || null
    case 'pacbio_bam':
      return row.pacbio_bam_id || null
    default:
      return row.fastq_pair_id || row.vcf_id || row.ont_bam_id || row.pacbio_bam_id || row.id || null
  }
}

export const fileNamesOf = (row) => {
  if (!row) return []
  switch (row.file_type) {
    case 'fastq':
      return [row.fastq1_filename, row.fastq2_filename].filter(Boolean)
    case 'vcf':
      return [row.vcf_filename].filter(Boolean)
    case 'ont_bam':
      return [row.ont_bam_filename].filter(Boolean)
    case 'pacbio_bam':
      return [row.pacbio_bam_filename].filter(Boolean)
    default:
      return [row.name].filter(Boolean)
  }
}

export const sampleNameOf = (row) => {
  if (!row) return ''
  if (row.sample) return String(row.sample)
  if (row.file_type === 'fastq' && row.fastq1_filename) return fastqSampleName(row.fastq1_filename)
  return fileNamesOf(row)[0] || ''
}

// Mirrors the wizard's gating (StepSelectFile.isFileDisabled): VCFs need
// is_verified; FASTQ, ONT and PacBio need is_processed, then is_verified.
export const fileStateOf = (row) => {
  if (!row) return 'missing'
  if (row.file_type === 'vcf') {
    if (row.is_verified) return 'ready'
    // A VCF the server refused will never verify: not "verifying" forever.
    if (row.refusal_reason) return 'unverified'
    return row.is_processed ? 'unverified' : 'verifying'
  }
  if (!row.is_processed) return 'verifying'
  if (!row.is_verified) return 'unverified'
  return 'ready'
}

export const isSingleReadRow = (row) => row?.file_type === 'fastq' && !row.fastq2_filename

// Lanes of one sample share a root once the lane token (_L001 Illumina, _L01 MGI)
// and the Illumina sample number just before it (_S1) are stripped. Names without a
// lane token are never grouped, so two uploads of the same pair stay two samples.
const LANE_TOKEN_RE = /[._-]L0*\d{1,3}(?=$|[._-])/i
export const laneRoot = (sampleName) => {
  const name = String(sampleName || '')
  if (!LANE_TOKEN_RE.test(name)) return null
  const root = name.replace(LANE_TOKEN_RE, '').replace(/[._-]S\d+$/i, '')
  return root || null
}

// Case- and separator-insensitive form of a name, for matching.
export const looseKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\.(fastq|fq)(\.gz)?$|\.vcf(\.gz)?$|\.bam$/i, '')
    .replace(/[^a-z0-9]/g, '')

/**
 * Groups /file/list rows into candidate samples: FASTQ lane pairs of one sample
 * become one candidate, every other row is its own candidate.
 * @returns {Array<{ key, fileType, rows }>}
 */
export const groupRows = (rows = []) => {
  const out = []
  const byRoot = new Map()
  rows.forEach((row) => {
    if (!fileIdOf(row)) return
    // A sub-organisation's upload, listed to its parent: visible, never startable here.
    if (row.can_start === false) return
    const name = sampleNameOf(row)
    if (row.file_type === 'fastq') {
      const root = laneRoot(name)
      if (root) {
        const k = root.toLowerCase()
        const existing = byRoot.get(k)
        if (existing) {
          existing.rows.push(row)
          return
        }
        const candidate = { key: root, fileType: 'fastq', rows: [row], laneGroup: true }
        byRoot.set(k, candidate)
        out.push(candidate)
        return
      }
    }
    out.push({ key: name, fileType: row.file_type, rows: [row], laneGroup: false })
  })
  out.forEach((candidate) => {
    candidate.rows.sort((a, b) => sampleNameOf(a).localeCompare(sampleNameOf(b)))
    candidate.id = fileIdOf(candidate.rows[0])
  })
  return out
}

const worstState = (states) => {
  if (states.includes('missing')) return 'missing'
  if (states.includes('unverified')) return 'unverified'
  if (states.includes('verifying')) return 'verifying'
  return 'ready'
}

// Live facts about a sample's files, from the current file list (files verify while
// the panel is open).
export const describeFiles = (sample, fileMap) => {
  const rows = (sample.fileIds || []).map((id) => fileMap.get(id)).filter(Boolean)
  const state = rows.length ? worstState(rows.map(fileStateOf)) : 'missing'
  const single = rows.some(isSingleReadRow)
  const names = rows.length ? rows.flatMap(fileNamesOf) : sample.files || []
  let summary = FILE_TYPE_LABEL[sample.fileType] || ''
  // `short` fits the Files column of the batch table.
  let short = summary
  if (sample.fileType === 'fastq') {
    summary = single ? 'R1 only' : 'R1 R2'
    short = rows.length > 1 ? `${rows.length} lanes` : summary
    if (rows.length > 1) summary += ` · ${rows.length} lanes`
  } else if (sample.fileType === 'vcf') {
    const vt = sample.variantType
    summary = vt === 'cnv' ? 'CNV VCF' : vt === 'sv' ? 'SV VCF' : 'SNV/Indel VCF'
    short = vt === 'cnv' ? 'CNV' : vt === 'sv' ? 'SV' : 'SNV/Indel'
  }
  return { rows, state, single, names, summary, short, lanes: sample.fileType === 'fastq' ? rows.length || 1 : 1 }
}

const detailsOf = (row) => row?.details || {}

/** A new sample from a candidate (see groupRows). */
export const sampleFromCandidate = (candidate) => {
  const first = candidate.rows[0]
  const details = detailsOf(first)
  const fileIds = candidate.rows.map(fileIdOf)
  return {
    uid: fileIds[0],
    key: candidate.key,
    fileId: fileIds[0],
    fileIds,
    fileType: candidate.fileType,
    variantType: candidate.fileType === 'vcf' ? first.variant_type || 'snp_indel' : undefined,
    methylationReady:
      candidate.fileType === 'pacbio_bam' ? first.methylation_available === 'methylation_ready' : undefined,
    files: candidate.rows.flatMap(fileNamesOf),
    lanes: candidate.fileType === 'fastq' ? candidate.rows.length : 1,
    laneGroup: !!candidate.laneGroup,
    alias: '',
    // ONT and PacBio rows carry saved patient details; FASTQ and VCF rows do not.
    sex: details.sex || '',
    age: details.age ?? '',
    onsetAge: details.symptoms_start_age ?? '',
    notes: details.notes || '',
    hpo: [],
    clinicalHistory: '',
    aiGuidance: '',
    tumourType: null,
    // Somatic specimen metadata (roadmap R1.2a), all optional; sex and age come from
    // the patient fields above. See specimenOf in services/analysisStart.
    specimen: { tumourType: '', tumourPercent: '', tumourCollectionDate: '', normalType: '', normalCollectionDate: '' },
    // Other analyses of the same patient, somatic only: { analysis_id, ... } rows.
    linkedAnalyses: [],
    normalFileId: null,
    normalKey: null,
    normalSuggested: false,
    changed: {},
    // AI: `ai` is this sample's own choice (true or false); left out, the sample
    // follows the batch's interpretation. See effectiveAI.
    excludeAI: false,
    excludeCNV: false,
    // ONT and PacBio: this sample's own CNV / SV / methylation, over the batch's.
    longRead: null,
    sheetErrors: {},
  }
}

/**
 * Adds candidates to the sample list. A lane group joins an existing sample with
 * the same root; a file already in the list is skipped.
 * @returns {{ samples, added: number }}
 */
export const addCandidates = (samples, candidates) => {
  const next = samples.map((s) => ({ ...s }))
  const inUse = new Set(next.flatMap((s) => s.fileIds || []))
  let added = 0
  candidates.forEach((candidate) => {
    const rows = candidate.rows.filter((row) => !inUse.has(fileIdOf(row)))
    if (!rows.length) return
    if (candidate.laneGroup) {
      const target = next.find(
        (s) => s.laneGroup && s.fileType === 'fastq' && s.key.toLowerCase() === candidate.key.toLowerCase(),
      )
      if (target) {
        rows.forEach((row) => {
          target.fileIds = [...target.fileIds, fileIdOf(row)]
          target.files = [...target.files, ...fileNamesOf(row)]
        })
        target.lanes = target.fileIds.length
        rows.forEach((row) => inUse.add(fileIdOf(row)))
        return
      }
    }
    const sample = sampleFromCandidate({ ...candidate, rows })
    rows.forEach((row) => inUse.add(fileIdOf(row)))
    next.push(sample)
    added += 1
  })
  return { samples: next, added }
}

/**
 * Merges FASTQ samples into one sample with several lanes (the wizard's
 * "Demultiplexed Sample"), for lanes whose names do not group on their own. The
 * first of `uids` in list order keeps its details; the others are dropped. Fewer
 * than two FASTQ samples among `uids` changes nothing. Offered where lanesAllowed:
 * germline and somatic DNA FASTQ (tumour RNA takes one pair per sample).
 */
export const mergeSamples = (samples, uids) => {
  const wanted = new Set(uids instanceof Set ? uids : Array.isArray(uids) ? uids : [])
  const targets = samples.filter((s) => wanted.has(s.uid) && s.fileType === 'fastq')
  if (targets.length < 2) return samples
  const [first, ...rest] = targets
  const fileIds = [...new Set(targets.flatMap((s) => (s.fileIds && s.fileIds.length ? s.fileIds : [s.fileId])))]
  const merged = {
    ...first,
    fileIds,
    files: targets.flatMap((s) => s.files || []),
    lanes: fileIds.length,
    laneGroup: true,
    manualLanes: true,
  }
  const dropped = new Set(rest.map((s) => s.uid))
  return samples.filter((s) => !dropped.has(s.uid)).map((s) => (s.uid === first.uid ? merged : s))
}

/** Splits a lane group (found by name or merged by hand) back into one sample per lane pair. */
export const splitLanes = (samples, uid, fileMap) => {
  const index = samples.findIndex((s) => s.uid === uid)
  if (index < 0) return samples
  const { manualLanes, ...sample } = samples[index]
  const parts = sample.fileIds.map((id, i) => {
    const row = fileMap.get(id)
    const base = i === 0 ? { ...sample } : sampleFromCandidate({ key: '', fileType: 'fastq', rows: [row || {}] })
    return {
      ...base,
      uid: id,
      key: row ? sampleNameOf(row) : `${sample.key}_${i + 1}`,
      fileId: id,
      fileIds: [id],
      files: row ? fileNamesOf(row) : [],
      lanes: 1,
      laneGroup: false,
    }
  })
  return [...samples.slice(0, index), ...parts, ...samples.slice(index + 1)]
}

// ----------------------------------------------------------------------
// Tumour and normal pairing by name: _T/_N, -tumour/-normal, -FFPE/-blood.

const TUMOUR_SUFFIX_RE = /[._-](t|tumou?r|ffpe)$/i
const NORMAL_SUFFIX_RE = /[._-](n|normal|blood|kan)$/i

export const tumourStem = (name) => {
  const s = String(name || '')
  return TUMOUR_SUFFIX_RE.test(s) ? s.replace(TUMOUR_SUFFIX_RE, '').toLowerCase() : null
}
export const normalStem = (name) => {
  const s = String(name || '')
  return NORMAL_SUFFIX_RE.test(s) ? s.replace(NORMAL_SUFFIX_RE, '').toLowerCase() : null
}
export const looksNormal = (name) => normalStem(name) !== null

/**
 * The normal a tumour's name points at, among FASTQ candidates (a normal's lanes are
 * one candidate).
 * @returns candidate or null
 */
export const suggestNormal = (sample, candidates) => {
  const stem = tumourStem(sample.key)
  if (!stem) return null
  return candidates.find((c) => c.fileType === 'fastq' && c.id !== sample.fileId && normalStem(c.key) === stem) || null
}

/**
 * The sample fields that pick a normal: a candidate (see groupRows) or a normal
 * option ({ id, key, fileIds }), every lane of it. null clears the normal.
 */
export const normalFieldsOf = (normal) => {
  if (!normal) return { normalFileId: null, normalFileIds: null, normalKey: null }
  const fileIds = normal.rows ? normal.rows.map(fileIdOf) : normal.fileIds
  return {
    normalFileId: normal.id,
    normalFileIds: Array.isArray(fileIds) && fileIds.length ? fileIds : [normal.id],
    normalKey: normal.key,
  }
}

// ----------------------------------------------------------------------
// Row issues. `blocking` issues keep a sample out of the start; the others are
// shown so the user can fix them, and do not block.

export const hasHistory = (sample) => !!String(sample?.clinicalHistory || '').trim()
export const hpoCount = (sample) => (Array.isArray(sample?.hpo) ? sample.hpo.length : 0)
export const hasPhenotype = (sample) => hpoCount(sample) > 0 && hasHistory(sample)

// ----------------------------------------------------------------------
// AI per sample. sample.ai is a tri-state: true or false is the sample's own
// choice, undefined follows the batch's interpretation. The older excludeAI: true
// reads as false (drafts saved before per-sample AI). AI never runs on somatic
// analyses or CNV/SV VCFs.

/** True when AI runs for this sample (what the start sends as runAI). */
export const effectiveAI = (workflow, sample) => sampleRunsAI(workflow, sample)

/** True when AI can be switched on for this sample at all. */
export const canUseAI = (workflow, sample) => workflow?.analysis === 'germline' && aiAvailable(workflow, sample)

/** How many samples run with AI and how many without. */
export const aiCounts = (workflow, samples = []) => {
  const withAI = samples.filter((s) => effectiveAI(workflow, s)).length
  return { withAI, withoutAI: samples.length - withAI }
}

/**
 * The changeSample patch that turns AI on or off for a sample. A choice equal to the
 * batch's interpretation is not kept as the sample's own, so the sample follows the
 * batch again (as a long-read override does). excludeAI mirrors ai === false for
 * readers of the older field.
 * @returns {{ ai: boolean | undefined, excludeAI: boolean }}
 */
export const aiPatch = (workflow, on) => {
  const value = Boolean(on)
  const follows = value === (workflow?.interpretation === 'ai')
  return { ai: follows ? undefined : value, excludeAI: !follows && !value }
}

// What the Phenotype cell of the batch table says: "2 HPO", "History",
// "2 HPO · history", or '' when nothing is entered yet.
export const phenotypeSummary = (sample) => {
  const count = hpoCount(sample)
  const history = hasHistory(sample)
  if (count && history) return `${count} HPO · history`
  if (count) return `${count} HPO`
  return history ? 'History' : ''
}

// What the AI issue names as missing.
const missingPhenotypeText = (sample) => {
  const noHpo = !hpoCount(sample)
  const noHistory = !hasHistory(sample)
  if (noHpo && noHistory) return 'No HPO terms or clinical history'
  return noHpo ? 'No HPO terms' : 'No clinical history'
}

export const isCnvOrSvVcf = (sample) =>
  sample.fileType === 'vcf' && (sample.variantType === 'cnv' || sample.variantType === 'sv')

// Samples used as the normal of another sample are not analyses of their own in a
// tumour-normal batch. Every lane of a normal counts.
export const normalFileIdsInUse = (samples) =>
  new Set(samples.flatMap(normalLaneIdsOf).filter((id) => id !== null && id !== undefined && id !== ''))

// validateSample codes (src/services/analysisStart.js) and the issue that covers each.
const START_ISSUE_CODES = {
  no_file: 'missing',
  type_mismatch: 'type',
  somatic_vcf_type: 'somatic-vcf',
  somatic_lanes: 'somatic-lanes',
  no_normal: 'normal',
  ai_needs_phenotype: 'ai',
  methylation_not_ready: 'meth',
}

// Start codes whose fields only the full sample editor shows.
const DETAILS_FIX_CODES = new Set(['library', 'specimen'])

export const duplicateKeysOf = (samples) => {
  const seen = new Set()
  const dup = new Set()
  samples.forEach((s) => {
    const k = String(s.key || '').toLowerCase()
    if (seen.has(k)) dup.add(k)
    seen.add(k)
  })
  return dup
}

// ----------------------------------------------------------------------
// Batches by input. A batch runs one analysis on one input; the samples picked for
// another input are set aside, never dropped, and offered as their own batch.

const LONG_READ_OVERRIDE_FIELDS = { cnv: 'lrCnv', sv: 'lrSv', meth: 'lrMeth' }

const SET_ASIDE_NOTES = {
  longRead: 'Long reads run as germline.',
  cnvSvVcf: 'CNV and SV VCFs run as germline.',
}

/**
 * Splits samples into those this batch runs and set-aside groups:
 * { inBatch, setAside: [{ key: `${analysis}:${input}`, analysis, input, samples, note }] }.
 * Germline runs the workflow's input. Somatic runs FASTQ or SNV/Indel VCFs; long
 * reads and CNV/SV VCFs go to a germline batch, with a note. A sample of no known
 * type stays in the batch, where its issue shows. Group analyses have no file batch.
 * setAside is sorted by size, largest first.
 */
export const partitionSamples = (samples = [], workflow = {}) => {
  const inBatch = []
  const groups = new Map()
  if (GROUP_ANALYSES.includes(workflow?.analysis)) return { inBatch, setAside: [] }
  const somatic = workflow?.analysis === 'somatic'
  const setAside = (analysis, input, sample, note = null) => {
    const key = `${analysis}:${input}`
    if (!groups.has(key)) groups.set(key, { key, analysis, input, samples: [], note })
    groups.get(key).samples.push(sample)
  }
  samples.forEach((sample) => {
    const input = inputOfSample(sample)
    if (!input) inBatch.push(sample)
    else if (!somatic) {
      if (input === workflow.input) inBatch.push(sample)
      else setAside('germline', input, sample)
    } else if (input === 'ont' || input === 'pacbio') setAside('germline', input, sample, SET_ASIDE_NOTES.longRead)
    else if (isCnvOrSvVcf(sample)) setAside('germline', 'vcf', sample, SET_ASIDE_NOTES.cnvSvVcf)
    else if (input === workflow.input) inBatch.push(sample)
    else setAside('somatic', input, sample)
  })
  const order = [...groups.keys()]
  const sorted = [...groups.values()].sort(
    (a, b) => b.samples.length - a.samples.length || order.indexOf(a.key) - order.indexOf(b.key),
  )
  return { inBatch, setAside: sorted }
}

/**
 * The changeSample patch for a long-read sample's own CNV, SV or methylation
 * ('cnv' | 'sv' | 'meth'). A value equal to the batch's drops the override, and no
 * override left is null.
 * @returns {{ longRead: object | null }}
 */
export const setLongReadOverride = (sample, field, value, workflow) => {
  const batchField = LONG_READ_OVERRIDE_FIELDS[field]
  const own = { ...(sample?.longRead && typeof sample.longRead === 'object' ? sample.longRead : {}) }
  if (!batchField) return { longRead: Object.keys(own).length ? own : null }
  const batchValue = Boolean({ ...WORKFLOW_DEFAULTS, ...(workflow || {}) }[batchField])
  if (Boolean(value) === batchValue) delete own[field]
  else own[field] = Boolean(value)
  return { longRead: Object.keys(own).length ? own : null }
}

// The input most samples feed, for a mixed selection.
export const majorityInput = (samples) => {
  const counts = {}
  samples.forEach((s) => {
    const input = INPUT_OF_FILE_TYPE[s.fileType]
    if (input) counts[input] = (counts[input] || 0) + 1
  })
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
  return { input: entries[0]?.[0] || null, counts }
}

// Parse a number field ("34", "34,5"); '' when empty, NaN when not a number from
// 0 to 120.
export const parseAge = (value) => {
  if (value === null || value === undefined) return ''
  const s = String(value).trim()
  if (!s) return ''
  const m = s.replace(',', '.').match(/^(\d{1,3}(?:\.\d+)?)\s*(y|yr|yrs|years?|ya[sş]|yaşında)?$/i)
  if (!m) return NaN
  const n = Number(m[1])
  return n >= 0 && n <= 120 ? n : NaN
}

const AGE_FIELDS = [
  { field: 'age', label: 'Age' },
  { field: 'onsetAge', label: 'Age at onset' },
]

// A sample as the start sends it. Ages are kept as typed, so a valid entry not yet
// rewritten on blur ("34,5", "34 y": restored from a draft, or Review opened by the
// shortcut) becomes its number here; the start reads it as it reads a typed number.
// Empty and invalid values are left as they are (an invalid age blocks the start).
export const withParsedAges = (sample) => {
  let next = sample
  AGE_FIELDS.forEach(({ field }) => {
    const parsed = parseAge(sample[field])
    if (typeof parsed !== 'number' || Number.isNaN(parsed) || String(parsed) === String(sample[field])) return
    if (next === sample) next = { ...sample }
    next[field] = parsed
  })
  return next
}

export const computeIssues = (sample, { workflow, fileMap, duplicateKeys, rnaCapture }) => {
  const issues = []
  const files = describeFiles(sample, fileMap)
  const input = INPUT_OF_FILE_TYPE[sample.fileType]
  const somatic = workflow.analysis === 'somatic'

  if (input !== workflow.input) {
    issues.push({
      code: 'type',
      blocking: true,
      text: `${FILE_TYPE_LABEL[sample.fileType] || 'This'} file. This batch runs ${
        { fastq: 'FASTQ', vcf: 'VCF', ont: 'ONT BAM', pacbio: 'PacBio BAM' }[workflow.input]
      }`,
      fixes: ['remove'],
    })
    return issues
  }
  if (files.state === 'missing') {
    issues.push({
      code: 'missing',
      blocking: true,
      text: 'File not found. It may have been deleted',
      fixes: ['remove'],
    })
    return issues
  }
  if (files.single) {
    issues.push({ code: 'r2', blocking: true, text: 'R2 missing', fixes: ['remove'] })
  }
  if (files.state === 'verifying') {
    issues.push({ code: 'verifying', blocking: true, text: 'Files are still being verified', fixes: [] })
  } else if (files.state === 'unverified') {
    issues.push({ code: 'unverified', blocking: true, text: 'Files failed verification', fixes: ['remove'] })
  }
  if (somatic && isCnvOrSvVcf(sample)) {
    issues.push({
      code: 'somatic-vcf',
      blocking: true,
      text: 'Somatic runs take SNV/Indel VCFs only',
      fixes: ['remove'],
    })
  }
  // Tumour RNA takes one FASTQ pair; the lanes of a tumour or normal DNA sample run
  // together, as germline lanes do.
  if (somatic && isTumourRna(workflow) && files.lanes > 1) {
    issues.push({
      code: 'somatic-lanes',
      blocking: true,
      text: 'Tumour RNA takes one FASTQ pair per sample',
      fixes: ['split'],
    })
  }
  if (somatic && workflow.input === 'fastq' && workflow.mode === 'tn') {
    if (!sample.normalFileId) {
      issues.push({ code: 'normal', blocking: true, text: 'No normal sample', fixes: [] })
    } else {
      // Every lane of the normal must be there and verified.
      const normalRows = normalLaneIdsOf(sample).map((id) => fileMap.get(id))
      if (normalRows.some((row) => !row)) {
        issues.push({ code: 'normal-missing', blocking: true, text: 'Normal sample not found', fixes: [] })
      } else if (normalRows.some((row) => fileStateOf(row) !== 'ready')) {
        issues.push({ code: 'normal-verifying', blocking: true, text: 'Normal sample is not verified yet', fixes: [] })
      } else if (sample.normalSuggested) {
        issues.push({
          code: 'normal-suggested',
          blocking: false,
          text: `Normal suggested from the names: ${sample.normalKey}`,
          fixes: ['confirmNormal'],
        })
      }
    }
  }
  // Only samples that run AI need HPO terms and clinical history.
  if (effectiveAI(workflow, sample) && !hasPhenotype(sample)) {
    issues.push({ code: 'ai', blocking: true, text: missingPhenotypeText(sample), fixes: ['addPhenotype', 'aiOff'] })
  }
  // Ages are kept as typed; one that is not a number from 0 to 120 keeps the sample
  // out. Long-read files show their saved details read-only, so only editable ones.
  AGE_FIELDS.forEach(({ field, label }) => {
    if (canSavePatient(sample) && Number.isNaN(parseAge(sample[field]))) {
      issues.push({
        code: `${field}-invalid`,
        blocking: true,
        text: `${label} must be a number from 0 to 120`,
        fixes: ['clear'],
        field,
      })
    }
  })
  if (workflow.input === 'pacbio' && requestedLongRead(workflow, sample).meth && !sample.methylationReady) {
    issues.push({ code: 'meth', blocking: false, text: 'No methylation data in this file. Methylation is skipped' })
  }
  if (duplicateKeys && duplicateKeys.has(sample.key.toLowerCase())) {
    issues.push({ code: 'dup', blocking: false, text: 'Two samples share this name' })
  }
  Object.entries(sample.sheetErrors || {}).forEach(([field, text]) => {
    issues.push({ code: `sheet-${field}`, blocking: false, text, fixes: ['dismissSheet'], field })
  })
  // The start module checks each sample again before sending it; anything it would
  // refuse that is not covered above keeps the sample out here too.
  const covered = new Set(issues.map((i) => i.code))
  validateSample(workflow, sample, { rnaCapture })
    .filter((problem) => problem.blocking && !covered.has(START_ISSUE_CODES[problem.code]))
    .forEach((problem) => {
      issues.push({
        code: `start-${problem.code}`,
        blocking: true,
        text: problem.message,
        // Specimen and RNA library fields live in the full sample editor, which
        // the table row opens in a dialog.
        fixes: DETAILS_FIX_CODES.has(problem.code) ? ['details'] : [],
      })
    })
  return issues
}
